/**
 * <CameraScanButton /> — one-tap camera capture for UK plates and VINs.
 *
 * Pattern: a small icon-button that, when tapped, fires a hidden
 * <input type="file" accept="image/*" capture="environment"> — on mobile this
 * opens the native camera; on desktop it falls back to a file picker. The
 * picked photo is downscaled & re-encoded as JPEG (max 1280px) before being
 * sent to the backend so we never push multi-MB payloads through the proxy.
 *
 * Usage:
 *   <CameraScanButton mode="plate" onScanned={(text) => setRegistration(text)} t={t} />
 *   <CameraScanButton mode="vin"   onScanned={(text) => setVinNumber(text)}    t={t} />
 */
import React, { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, Loader2, Check, AlertTriangle, Sun, AlignCenter, Focus } from "lucide-react";
import axios from "axios";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const MAX_DIM = 1280;
const JPEG_QUALITY = 0.82;
const TUTORIAL_STORAGE_KEY = "worthify_camera_tutorial_seen_v1";

/** Downscale + re-encode the camera capture as JPEG so payloads stay tight. */
const compressImage = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("read failed"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("image decode failed"));
      img.onload = () => {
        let { width, height } = img;
        const scale = Math.min(1, MAX_DIM / Math.max(width, height));
        width = Math.round(width * scale);
        height = Math.round(height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (!blob) return reject(new Error("encode failed"));
            const fr = new FileReader();
            fr.onload = () => {
              const dataUrl = fr.result;
              const b64 = String(dataUrl).split(",", 2)[1] || "";
              resolve(b64);
            };
            fr.onerror = () => reject(new Error("base64 failed"));
            fr.readAsDataURL(blob);
          },
          "image/jpeg",
          JPEG_QUALITY,
        );
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

export const CameraScanButton = ({ mode = "plate", onScanned, t, "data-testid": testid, compact = false }) => {
  const inputRef = useRef(null);
  // status: idle | uploading | success | error
  const [status, setStatus] = useState("idle");
  const [errMsg, setErrMsg] = useState("");

  const triggerInput = () => inputRef.current?.click();

  // Direct fire — user explicitly asked for "no intermediate screen, just open
  // the camera". The earlier coachmark UX was killing the muscle-memory click.
  const openCamera = () => triggerInput();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    setStatus("uploading");
    setErrMsg("");
    try {
      const b64 = await compressImage(file);
      const endpoint = mode === "vin" ? "/vision/scan-vin" : "/vision/scan-plate";
      const { data } = await axios.post(`${API_BASE}${endpoint}`, { image_base64: b64 }, { timeout: 30000 });
      const value = mode === "vin" ? data?.vin : data?.plate;
      if (!value) {
        setStatus("error");
        setErrMsg(
          mode === "vin"
            ? (t?.("scanVinNotFound") || "No VIN detected. Try a closer, well-lit shot.")
            : (t?.("scanPlateNotFound") || "No plate detected. Try a closer, well-lit shot."),
        );
        setTimeout(() => setStatus("idle"), 2800);
        return;
      }
      setStatus("success");
      onScanned?.(value);
      setTimeout(() => setStatus("idle"), 1400);
    } catch (err) {
      setStatus("error");
      setErrMsg(t?.("scanGenericError") || "Couldn't read the image. Try again.");
      setTimeout(() => setStatus("idle"), 2800);
    }
  };

  const baseColor = mode === "vin" ? "#6366F1" : "#10B981";
  const ariaLabel = mode === "vin"
    ? (t?.("scanVinAria") || "Scan VIN with camera")
    : (t?.("scanPlateAria") || "Scan plate with camera");

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        capture="environment"
        onChange={handleFile}
        style={{ display: "none" }}
        data-testid={`${testid || `scan-${mode}`}-file-input`}
      />
      <motion.button
        type="button"
        whileTap={{ scale: 0.92 }}
        onClick={openCamera}
        disabled={status === "uploading"}
        aria-label={ariaLabel}
        title={ariaLabel}
        style={{
          flexShrink: 0,
          width: compact ? 36 : 42, height: compact ? 36 : 42,
          borderRadius: 999, border: "none",
          background: status === "success"
            ? "linear-gradient(135deg,#10B981,#059669)"
            : status === "error"
              ? "linear-gradient(135deg,#EF4444,#DC2626)"
              : `linear-gradient(135deg, ${baseColor}, ${baseColor}cc)`,
          color: "var(--wf-modal-text)", cursor: status === "uploading" ? "wait" : "pointer",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          boxShadow: `0 6px 14px ${baseColor}55`,
          transition: "background 200ms ease",
        }}
        data-testid={testid || `scan-${mode}-btn`}
      >
        {status === "uploading" && <Loader2 className="w-5 h-5 animate-spin" strokeWidth={2.6} />}
        {status === "success" && <Check className="w-5 h-5" strokeWidth={2.8} />}
        {status === "error" && <AlertTriangle className="w-5 h-5" strokeWidth={2.6} />}
        {status === "idle" && <Camera className="w-5 h-5" strokeWidth={2.4} />}
      </motion.button>
      {/* Inline error/help text directly below the button (small, mobile-friendly) */}
      {status === "error" && errMsg && (
        <p
          role="alert"
          style={{
            position: "absolute", marginTop: 6, fontSize: 11, color: "#DC2626",
            fontWeight: 600, lineHeight: 1.35,
            background: "white", padding: "4px 8px", borderRadius: 8,
            border: "1px solid rgba(220,38,38,0.25)", whiteSpace: "nowrap",
            zIndex: 10, transform: "translateX(-50%)",
            left: "50%",
            top: compact ? 36 : 42,
          }}
          data-testid={`${testid || `scan-${mode}`}-error`}
        >
          {errMsg}
        </p>
      )}
    </>
  );
};

/**
 * First-run coachmark for the camera. Bottom-sheet style on mobile, centered
 * modal on tablet+. Three crisp tips, one CTA. Auto-persist "seen" via
 * dismissTutorial so we never block the seasoned user again.
 */
const CameraScanTutorial = ({ mode, t, onDismiss }) => {
  const isPlate = mode !== "vin";
  const title = isPlate
    ? (t?.("scanTutorialPlateTitle") || "Get the perfect plate shot")
    : (t?.("scanTutorialVinTitle") || "Get the perfect VIN shot");
  const subtitle = isPlate
    ? (t?.("scanTutorialPlateSubtitle") || "Three quick tips for instant detection.")
    : (t?.("scanTutorialVinSubtitle") || "VINs are small — keep the camera steady.");

  const tips = isPlate
    ? [
        { icon: AlignCenter, text: t?.("scanTipCenter") || "Centre the plate in the frame" },
        { icon: Sun,         text: t?.("scanTipLight")  || "Make sure it's well-lit (no shadows on the plate)" },
        { icon: Focus,       text: t?.("scanTipClose")  || "Get close — the plate should fill most of the photo" },
      ]
    : [
        { icon: AlignCenter, text: t?.("scanTipVinLocation") || "VIN is on the dashboard (visible through the windscreen) or door jamb" },
        { icon: Sun,         text: t?.("scanTipLight")        || "Make sure it's well-lit (no shadows)" },
        { icon: Focus,       text: t?.("scanTipVinClose")     || "Get close — the 17 characters should fill most of the photo" },
      ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={() => onDismiss(false)}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "var(--wf-modal-card-bg)", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "flex-end", justifyContent: "center",
        padding: 16,
      }}
      data-testid={`scan-${mode}-tutorial-overlay`}
    >
      <motion.div
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 60, opacity: 0 }}
        transition={{ type: "spring", stiffness: 340, damping: 28 }}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(420px, 100%)",
          background: "white", borderRadius: 22,
          padding: "20px 18px 18px",
          boxShadow: "0 24px 60px rgba(15,23,42,0.45)",
          marginBottom: "env(safe-area-inset-bottom, 0px)",
        }}
        data-testid={`scan-${mode}-tutorial`}
      >
        <div className="flex items-center gap-3 mb-3">
          <div style={{
            width: 40, height: 40, borderRadius: 12,
            background: isPlate
              ? "linear-gradient(135deg,#10B981,#059669)"
              : "linear-gradient(135deg,#6366F1,#4F46E5)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "var(--wf-modal-text)", boxShadow: "0 6px 14px rgba(15,23,42,0.18)",
          }}>
            <Camera className="w-5 h-5" strokeWidth={2.4} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 style={{ margin: 0, fontWeight: 800, fontSize: 16, letterSpacing: "-0.2px", color: "#0F172A" }}>
              {title}
            </h3>
            <p style={{ margin: 0, fontSize: 12, color: "#64748B" }}>{subtitle}</p>
          </div>
        </div>

        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {tips.map((tip, i) => {
            const Icon = tip.icon;
            return (
              <motion.li
                key={i}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.08 + i * 0.06 }}
                style={{
                  display: "flex", alignItems: "flex-start", gap: 10,
                  padding: "10px 0",
                  borderBottom: i < tips.length - 1 ? "1px solid #F1F5F9" : "none",
                }}
              >
                <div style={{
                  flexShrink: 0, width: 28, height: 28, borderRadius: 8,
                  background: isPlate ? "rgba(16,185,129,0.10)" : "rgba(99,102,241,0.10)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: isPlate ? "#10B981" : "#6366F1",
                }}>
                  <Icon className="w-4 h-4" strokeWidth={2.3} />
                </div>
                <span style={{ fontSize: 13, color: "#0F172A", lineHeight: 1.45 }}>{tip.text}</span>
              </motion.li>
            );
          })}
        </ul>

        <motion.button
          whileTap={{ scale: 0.97 }}
          onClick={() => onDismiss(true)}
          style={{
            width: "100%", marginTop: 14, padding: "12px 14px",
            border: "none", borderRadius: 12, cursor: "pointer",
            background: isPlate
              ? "linear-gradient(135deg,#10B981,#059669)"
              : "linear-gradient(135deg,#6366F1,#4F46E5)",
            color: "var(--wf-modal-text)", fontWeight: 800, fontSize: 13.5, letterSpacing: "-0.1px",
            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
            boxShadow: isPlate ? "0 8px 20px rgba(16,185,129,0.32)" : "0 8px 20px rgba(99,102,241,0.32)",
          }}
          data-testid={`scan-${mode}-tutorial-cta`}
        >
          <Camera className="w-4 h-4" strokeWidth={2.6} />
          {t?.("scanTutorialCta") || "Got it — open camera"}
        </motion.button>
        <button
          onClick={() => onDismiss(false)}
          style={{
            width: "100%", marginTop: 8, padding: "8px 14px",
            background: "transparent", border: "none", cursor: "pointer",
            color: "#64748B", fontSize: 12, fontWeight: 600,
          }}
          data-testid={`scan-${mode}-tutorial-skip`}
        >
          {t?.("scanTutorialSkip") || "Cancel"}
        </button>
      </motion.div>
    </motion.div>
  );
};

export default CameraScanButton;
